import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, db } from './firebase';
import { doc, getDoc, updateDoc, increment, collection, getDocs, runTransaction, query, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import type { MatchDocument, Question, Answer, YesNoQuestion, MultipleChoiceQuestion, YesNoValue, FlipData, MessageDocument, MessageStatus, MessageEditHistoryEntry } from './types';
import { MatchPhase } from './types';
import { validateYesNoQuestion, validateMultipleChoiceQuestion } from './validation';

type Category = 'love' | 'habits' | 'ambitions' | 'spirituality' | 'emotions';
type BeaconType = 'I' | 'You' | 'We';

interface BeaconObject {
  text: string;
  type: BeaconType;
}

interface BeaconsData {
  love: BeaconObject[];
  habits: BeaconObject[];
  ambitions: BeaconObject[];
  spirituality: BeaconObject[];
  emotions: BeaconObject[];
}

interface MatchedUserProfile {
  uid: string;
  name: string;
  beacon: BeaconsData;
}

const categories: { key: Category; label: string }[] = [
  { key: 'love', label: 'Love & Sensuality' },
  { key: 'habits', label: 'Habits & Personality' },
  { key: 'ambitions', label: 'Ambitions & Preferences' },
  { key: 'spirituality', label: 'Spirituality & Values' },
  { key: 'emotions', label: 'Emotions & Communication' }
];

const getTypeColor = (type: BeaconType): string => {
  if (type === 'I') return '#2196F3';
  if (type === 'You') return '#f44336';
  if (type === 'We') return '#9C27B0';
  throw new Error('Unknown beacon type');
};

const getTypeBackgroundColor = (type: BeaconType): string => {
  if (type === 'I') return '#E3F2FD';
  if (type === 'You') return '#FFEBEE';
  if (type === 'We') return '#F3E5F5';
  throw new Error('Unknown beacon type');
};

function MatchDetail(): React.ReactElement {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const { matchedUserId } = useParams<{ matchedUserId: string }>();

  const [matchDoc, setMatchDoc] = useState<MatchDocument | null>(null);
  const [matchedUser, setMatchedUser] = useState<MatchedUserProfile | null>(null);
  const [reverseMatchDoc, setReverseMatchDoc] = useState<MatchDocument | null>(null);
  const [myBeacons, setMyBeacons] = useState<BeaconsData | null>(null);
  const [viewMode, setViewMode] = useState<'their_beacons' | 'my_beacons' | 'their_questions' | 'conversation'>('their_beacons');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flipping, setFlipping] = useState<string | null>(null);
  const [requestingBeacons, setRequestingBeacons] = useState(false);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [matchedUserAnswers, setMatchedUserAnswers] = useState<Record<string, Answer>>({});
  const [myAnswers, setMyAnswers] = useState<Record<string, Answer>>({});
  const [selectedQuestionCategories, setSelectedQuestionCategories] = useState<Record<string, boolean>>({});
  const [questionsLoading, setQuestionsLoading] = useState(false);

  const [messages, setMessages] = useState<MessageDocument[]>([]);
  const [draftMessage, setDraftMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [gracePeriodRemaining, setGracePeriodRemaining] = useState<Record<string, number>>({});
  const [showEditHistory, setShowEditHistory] = useState<string | null>(null);
  const gracePeriodIntervals = useRef<Record<string, NodeJS.Timeout>>({});

  const MIN_CHARS = 400;
  const MAX_CHARS = 4000;
  const GRACE_PERIOD_SECONDS = 300;
  const RATE_LIMIT_HOURS = 18;

  const getTotalBeaconCount = (beacons: BeaconsData): number => {
    return beacons.love.length + beacons.habits.length + beacons.ambitions.length +
           beacons.spirituality.length + beacons.emotions.length;
  };

  const getTotalFlipCount = (flips: FlipData | undefined): number => {
    if (!flips) return 0;
    return flips.love + flips.habits + flips.ambitions + flips.spirituality + flips.emotions;
  };

  const canSendMessage = (): boolean => {
    if (!matchDoc?.lastMessageSentAt) return true;
    const hoursSince = (Date.now() - matchDoc.lastMessageSentAt.toMillis()) / (1000 * 60 * 60);
    return hoursSince >= RATE_LIMIT_HOURS;
  };

  const getRateLimitTimeRemaining = (): string | null => {
    if (!matchDoc?.lastMessageSentAt) return null;
    const hoursSince = (Date.now() - matchDoc.lastMessageSentAt.toMillis()) / (1000 * 60 * 60);
    if (hoursSince >= RATE_LIMIT_HOURS) return null;

    const hoursRemaining = RATE_LIMIT_HOURS - hoursSince;
    const hours = Math.floor(hoursRemaining);
    const minutes = Math.floor((hoursRemaining - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  const isValidMessageLength = (text: string): boolean => {
    const length = text.length;
    return length >= MIN_CHARS && length <= MAX_CHARS;
  };

  const getCharacterCountColor = (length: number): string => {
    if (length < MIN_CHARS || length > MAX_CHARS) return '#d32f2f';
    if (length > MAX_CHARS * 0.9) return '#f57c00';
    return '#4caf50';
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !matchedUserId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        const matchDocRef = doc(db, 'users', user.uid, 'matches', matchedUserId);
        const matchSnapshot = await getDoc(matchDocRef);

        if (!matchSnapshot.exists()) {
          setError('Match not found');
          setLoading(false);
          return;
        }

        const matchData = matchSnapshot.data();
        const match: MatchDocument = {
          matchedUserId: matchData.matchedUserId,
          matchedUserName: matchData.matchedUserName,
          createdAt: matchData.createdAt,
          outdated: matchData.outdated || false,
          phase: matchData.phase || MatchPhase.QuestionsOnly,
          beaconShareRequested: matchData.beaconShareRequested || false,
          flips: matchData.flips || {
            love: 0,
            habits: 0,
            ambitions: 0,
            spirituality: 0,
            emotions: 0
          },
          lastMessageSentAt: matchData.lastMessageSentAt || null
        };
        setMatchDoc(match);

        const userDocRef = doc(db, 'users', matchedUserId);
        const userSnapshot = await getDoc(userDocRef);

        if (!userSnapshot.exists()) {
          setError('Matched user profile not found');
          setLoading(false);
          return;
        }

        const userData = userSnapshot.data();
        const profile: MatchedUserProfile = {
          uid: userData.uid,
          name: userData.name,
          beacon: userData.beacon || {
            love: [],
            habits: [],
            ambitions: [],
            spirituality: [],
            emotions: []
          }
        };
        setMatchedUser(profile);

        const myDocRef = doc(db, 'users', user.uid);
        const mySnapshot = await getDoc(myDocRef);
        if (mySnapshot.exists()) {
          const myData = mySnapshot.data();
          setMyBeacons(myData.beacon || {
            love: [],
            habits: [],
            ambitions: [],
            spirituality: [],
            emotions: []
          });
        }

        const reverseMatchDocRef = doc(db, 'users', matchedUserId, 'matches', user.uid);
        const reverseMatchSnapshot = await getDoc(reverseMatchDocRef);
        if (reverseMatchSnapshot.exists()) {
          const reverseMatchData = reverseMatchSnapshot.data();
          const reverseMatch: MatchDocument = {
            matchedUserId: reverseMatchData.matchedUserId,
            matchedUserName: reverseMatchData.matchedUserName,
            createdAt: reverseMatchData.createdAt,
            outdated: reverseMatchData.outdated || false,
            phase: reverseMatchData.phase || MatchPhase.QuestionsOnly,
            beaconShareRequested: reverseMatchData.beaconShareRequested || false,
            flips: reverseMatchData.flips || {
              love: 0,
              habits: 0,
              ambitions: 0,
              spirituality: 0,
              emotions: 0
            }
          };
          setReverseMatchDoc(reverseMatch);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching match data:', err);
        setError('Failed to load match data');
        setLoading(false);
      }
    };

    fetchData();
  }, [user, matchedUserId]);

  const transitionToPhase4 = useCallback(async () => {
    if (!user || !matchedUserId) return;

    try {
      await runTransaction(db, async (transaction) => {
        const myMatchRef = doc(db, 'users', user.uid, 'matches', matchedUserId);
        const theirMatchRef = doc(db, 'users', matchedUserId, 'matches', user.uid);

        transaction.update(myMatchRef, { phase: MatchPhase.Messaging });
        transaction.update(theirMatchRef, { phase: MatchPhase.Messaging });
      });

      setMatchDoc({ ...matchDoc!, phase: MatchPhase.Messaging });
      setReverseMatchDoc({ ...reverseMatchDoc!, phase: MatchPhase.Messaging });
    } catch (error) {
      console.error('Error transitioning to Phase 4:', error);
    }
  }, [user, matchedUserId, matchDoc, reverseMatchDoc]);

  const finalizeMessage = useCallback(async (messageId: string) => {
    if (!user || !matchedUserId) return;

    try {
      const myMessageRef = doc(db, 'users', user.uid, 'matches', matchedUserId, 'messages', messageId);
      const theirMessageRef = doc(db, 'users', matchedUserId, 'matches', user.uid, 'messages', messageId);
      const myMatchRef = doc(db, 'users', user.uid, 'matches', matchedUserId);

      const now = Timestamp.now();

      await runTransaction(db, async (transaction) => {
        transaction.update(myMessageRef, {
          status: 'sent' as MessageStatus,
          sentAt: now,
          gracePeriodEndsAt: null
        });
        transaction.update(theirMessageRef, {
          status: 'sent' as MessageStatus,
          sentAt: now,
          gracePeriodEndsAt: null
        });
        transaction.update(myMatchRef, { lastMessageSentAt: now });
      });
    } catch (error) {
      console.error('Error finalizing message:', error);
    }
  }, [user, matchedUserId]);

  const startGracePeriodCountdown = useCallback((messageId: string, gracePeriodEndsAt: Timestamp) => {
    if (gracePeriodIntervals.current[messageId]) {
      clearInterval(gracePeriodIntervals.current[messageId]);
    }

    const intervalId = setInterval(() => {
      const now = new Date();
      const secondsRemaining = Math.max(0, Math.floor((gracePeriodEndsAt.toMillis() - now.getTime()) / 1000));

      if (secondsRemaining <= 0) {
        clearInterval(intervalId);
        delete gracePeriodIntervals.current[messageId];
        finalizeMessage(messageId);
      } else {
        setGracePeriodRemaining(prev => ({ ...prev, [messageId]: secondsRemaining }));
      }
    }, 1000);

    gracePeriodIntervals.current[messageId] = intervalId;
  }, [finalizeMessage]);

  useEffect(() => {
    const checkPhase4Unlock = async () => {
      if (!matchDoc || !reverseMatchDoc || !matchedUser || !myBeacons) return;
      if (matchDoc.phase !== MatchPhase.BeaconsUnlocked) return;

      const myTotalBeacons = getTotalBeaconCount(myBeacons);
      const partnerTotalBeacons = getTotalBeaconCount(matchedUser.beacon);
      const myTotalFlips = getTotalFlipCount(matchDoc.flips);
      const partnerTotalFlips = getTotalFlipCount(reverseMatchDoc.flips);

      const iHaveRevealedAll = myTotalFlips >= partnerTotalBeacons;
      const partnerHasRevealedAll = partnerTotalFlips >= myTotalBeacons;

      if (iHaveRevealedAll && partnerHasRevealedAll) {
        await transitionToPhase4();
      }
    };

    checkPhase4Unlock();
  }, [matchDoc, reverseMatchDoc, matchedUser, myBeacons, transitionToPhase4]);

  useEffect(() => {
    if (!user || !matchedUserId || viewMode !== 'conversation') return;

    const messagesRef = collection(db, 'users', user.uid, 'matches', matchedUserId, 'messages');
    const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesList: MessageDocument[] = snapshot.docs.map(doc => doc.data() as MessageDocument);
      setMessages(messagesList);

      messagesList.forEach(msg => {
        if (msg.status === 'draft' && msg.gracePeriodEndsAt) {
          startGracePeriodCountdown(msg.messageId, msg.gracePeriodEndsAt);
        }
      });
    });

    return () => unsubscribe();
  }, [user, matchedUserId, viewMode, startGracePeriodCountdown]);

  useEffect(() => {
    const checkExpiredDrafts = async () => {
      const now = new Date();
      const expiredDrafts = messages.filter(m =>
        m.status === 'draft' &&
        m.gracePeriodEndsAt &&
        m.gracePeriodEndsAt.toDate() <= now
      );

      for (const draft of expiredDrafts) {
        await finalizeMessage(draft.messageId);
      }
    };

    checkExpiredDrafts();
  }, [messages, finalizeMessage]);

  useEffect(() => {
    const intervals = gracePeriodIntervals.current;
    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, []);

  useEffect(() => {
    const fetchQuestionsData = async () => {
      if (viewMode !== 'their_questions' || !user || !matchedUserId) return;
      if (questions.length > 0) return;

      setQuestionsLoading(true);
      try {
        const questionsSnapshot = await getDocs(collection(db, 'questions'));
        const questionsData: Question[] = questionsSnapshot.docs.map(doc => {
          const data = doc.data();
          const type = data.type || 'yes_no';

          if (type === 'multiple_choice') {
            validateMultipleChoiceQuestion(doc.id, data);
            return {
              id: doc.id,
              text: data.text,
              type: 'multiple_choice',
              options: data.options || [],
              category: Array.isArray(data.category) ? data.category : (data.category ? [data.category] : []),
              class: data.class || 1,
            } as MultipleChoiceQuestion;
          } else {
            validateYesNoQuestion(doc.id, data);
            return {
              id: doc.id,
              text: data.text,
              type: 'yes_no',
              category: Array.isArray(data.category) ? data.category : (data.category ? [data.category] : []),
              class: data.class || 1,
            } as YesNoQuestion;
          }
        });
        setQuestions(questionsData);

        const matchedUserDoc = await getDoc(doc(db, 'users', matchedUserId));
        if (matchedUserDoc.exists()) {
          const matchedAnswers = matchedUserDoc.data().answers || {};
          setMatchedUserAnswers(matchedAnswers);
        }

        const myDoc = await getDoc(doc(db, 'users', user.uid));
        if (myDoc.exists()) {
          const answers = myDoc.data().answers || {};
          setMyAnswers(answers);
        }

      } catch (err) {
        console.error('Error fetching questions data:', err);
      } finally {
        setQuestionsLoading(false);
      }
    };

    fetchQuestionsData();
  }, [viewMode, user, matchedUserId, questions.length]);

  useEffect(() => {
    if (questions.length === 0) return;

    const allCategories = Array.from(
      new Set(
        questions.flatMap(q =>
          q.category.length > 0 ? q.category : ['Uncategorized']
        )
      )
    );

    const newSelectedCategories: Record<string, boolean> = {};
    allCategories.forEach(cat => {
      if (selectedQuestionCategories[cat] === undefined) {
        newSelectedCategories[cat] = true;
      }
    });

    if (Object.keys(newSelectedCategories).length > 0) {
      setSelectedQuestionCategories(prev => ({ ...prev, ...newSelectedCategories }));
    }
  }, [questions, selectedQuestionCategories]);

  useEffect(() => {
    if (matchDoc && matchDoc.phase < MatchPhase.BeaconsUnlocked && viewMode !== 'their_questions') {
      setViewMode('their_questions');
    }
  }, [matchDoc, viewMode]);

  const handleFlip = async (category: Category) => {
    if (!user || !matchDoc || !matchedUserId) return;

    if (!canFlipInCategory(category)) {
      alert('You have reached your flip limit. Wait for your partner to flip more of your beacons.');
      return;
    }

    const currentCount = matchDoc.flips?.[category] || 0;
    const totalBeacons = matchedUser?.beacon[category].length || 0;

    if (currentCount >= totalBeacons) return;

    const flipKey = `${category}`;
    if (flipping === flipKey) return;

    setFlipping(flipKey);

    try {
      const matchDocRef = doc(db, 'users', user.uid, 'matches', matchedUserId);
      await updateDoc(matchDocRef, {
        [`flips.${category}`]: increment(1)
      });

      setMatchDoc({
        ...matchDoc,
        flips: {
          ...matchDoc.flips!,
          [category]: currentCount + 1
        }
      });
    } catch (err) {
      console.error('Error flipping beacon:', err);
    } finally {
      setFlipping(null);
    }
  };

  const handleBeaconShareRequest = async () => {
    if (!user || !matchDoc || !matchedUserId) return;

    setRequestingBeacons(true);

    try {
      await runTransaction(db, async (transaction) => {
        const myMatchRef = doc(db, 'users', user.uid, 'matches', matchedUserId);
        const theirMatchRef = doc(db, 'users', matchedUserId, 'matches', user.uid);

        const myMatchSnap = await transaction.get(myMatchRef);
        const theirMatchSnap = await transaction.get(theirMatchRef);

        if (!myMatchSnap.exists() || !theirMatchSnap.exists()) {
          throw new Error('Match documents not found');
        }

        const theirMatchData = theirMatchSnap.data();

        transaction.update(myMatchRef, { phase: MatchPhase.WaitingForPartner, beaconShareRequested: true });

        const partnerAlreadyRequested = theirMatchData.beaconShareRequested === true;

        if (partnerAlreadyRequested) {
          transaction.update(myMatchRef, { phase: MatchPhase.BeaconsUnlocked });
          transaction.update(theirMatchRef, { phase: MatchPhase.BeaconsUnlocked });
        }
      });

      const updatedMatchSnap = await getDoc(doc(db, 'users', user.uid, 'matches', matchedUserId));
      const updatedReverseMatchSnap = await getDoc(doc(db, 'users', matchedUserId, 'matches', user.uid));

      if (updatedMatchSnap.exists()) {
        const data = updatedMatchSnap.data();
        setMatchDoc({
          matchedUserId: data.matchedUserId,
          matchedUserName: data.matchedUserName,
          createdAt: data.createdAt,
          outdated: data.outdated || false,
          phase: data.phase || MatchPhase.QuestionsOnly,
          beaconShareRequested: data.beaconShareRequested || false,
          flips: data.flips || { love: 0, habits: 0, ambitions: 0, spirituality: 0, emotions: 0 },
          lastMessageSentAt: data.lastMessageSentAt || null
        });
      }

      if (updatedReverseMatchSnap.exists()) {
        const data = updatedReverseMatchSnap.data();
        setReverseMatchDoc({
          matchedUserId: data.matchedUserId,
          matchedUserName: data.matchedUserName,
          createdAt: data.createdAt,
          outdated: data.outdated || false,
          phase: data.phase || MatchPhase.QuestionsOnly,
          beaconShareRequested: data.beaconShareRequested || false,
          flips: data.flips || { love: 0, habits: 0, ambitions: 0, spirituality: 0, emotions: 0 }
        });
      }

    } catch (error) {
      console.error('Error requesting beacon share:', error);
      alert('Failed to request beacon sharing. Please try again.');
    } finally {
      setRequestingBeacons(false);
    }
  };

  const handleSendMessage = async () => {
    if (!user || !matchedUserId || !isValidMessageLength(draftMessage) || !canSendMessage()) return;

    setSendingMessage(true);
    try {
      const messageId = crypto.randomUUID();
      const now = Timestamp.now();
      const gracePeriodEndsAt = new Timestamp(now.seconds + GRACE_PERIOD_SECONDS, now.nanoseconds);

      const messageData: MessageDocument = {
        messageId,
        senderId: user.uid,
        senderName: user.displayName || 'Unknown',
        content: draftMessage,
        status: 'draft' as MessageStatus,
        createdAt: now,
        gracePeriodEndsAt,
        sentAt: null,
        editHistory: []
      };

      const myMessageRef = doc(db, 'users', user.uid, 'matches', matchedUserId, 'messages', messageId);
      const theirMessageRef = doc(db, 'users', matchedUserId, 'matches', user.uid, 'messages', messageId);

      await runTransaction(db, async (transaction) => {
        transaction.set(myMessageRef, messageData);
        transaction.set(theirMessageRef, messageData);
      });

      startGracePeriodCountdown(messageId, gracePeriodEndsAt);
      setDraftMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCancelMessage = async (messageId: string) => {
    if (!user || !matchedUserId) return;
    if (!window.confirm('Cancel this message?')) return;

    try {
      const myMessageRef = doc(db, 'users', user.uid, 'matches', matchedUserId, 'messages', messageId);
      const theirMessageRef = doc(db, 'users', matchedUserId, 'matches', user.uid, 'messages', messageId);

      await runTransaction(db, async (transaction) => {
        transaction.delete(myMessageRef);
        transaction.delete(theirMessageRef);
      });

      if (gracePeriodIntervals.current[messageId]) {
        clearInterval(gracePeriodIntervals.current[messageId]);
        delete gracePeriodIntervals.current[messageId];
      }

      setGracePeriodRemaining(prev => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });
    } catch (error) {
      console.error('Error canceling message:', error);
      alert('Failed to cancel message.');
    }
  };

  const handleSaveDraftEdit = async () => {
    if (!user || !matchedUserId || !editingMessageId || !isValidMessageLength(editContent)) return;

    try {
      const myMessageRef = doc(db, 'users', user.uid, 'matches', matchedUserId, 'messages', editingMessageId);
      const theirMessageRef = doc(db, 'users', matchedUserId, 'matches', user.uid, 'messages', editingMessageId);

      await runTransaction(db, async (transaction) => {
        transaction.update(myMessageRef, { content: editContent });
        transaction.update(theirMessageRef, { content: editContent });
      });

      setEditingMessageId(null);
      setEditContent('');
    } catch (error) {
      console.error('Error editing draft:', error);
      alert('Failed to edit message.');
    }
  };

  const handleSaveSentEdit = async () => {
    if (!user || !matchedUserId || !editingMessageId || !isValidMessageLength(editContent)) return;

    try {
      const myMessageRef = doc(db, 'users', user.uid, 'matches', matchedUserId, 'messages', editingMessageId);
      const theirMessageRef = doc(db, 'users', matchedUserId, 'matches', user.uid, 'messages', editingMessageId);

      const messageSnap = await getDoc(myMessageRef);
      if (!messageSnap.exists()) throw new Error('Message not found');

      const messageData = messageSnap.data() as MessageDocument;
      const now = Timestamp.now();

      const newEditEntry: MessageEditHistoryEntry = {
        content: messageData.content,
        editedAt: now
      };

      await runTransaction(db, async (transaction) => {
        transaction.update(myMessageRef, {
          content: editContent,
          status: 'edited' as MessageStatus,
          editHistory: [...messageData.editHistory, newEditEntry]
        });
        transaction.update(theirMessageRef, {
          content: editContent,
          status: 'edited' as MessageStatus,
          editHistory: [...messageData.editHistory, newEditEntry]
        });
      });

      setEditingMessageId(null);
      setEditContent('');
    } catch (error) {
      console.error('Error editing sent message:', error);
      alert('Failed to edit message.');
    }
  };

  const isFlipped = (category: Category, index: number): boolean => {
    const count = matchDoc?.flips?.[category] || 0;
    return index < count;
  };

  const isMyBeaconFlipped = (category: Category, index: number): boolean => {
    const count = reverseMatchDoc?.flips?.[category] || 0;
    return index < count;
  };

  const canFlipInCategory = (category: Category): boolean => {
    if (!matchDoc || !reverseMatchDoc || !matchedUser || !myBeacons) return false;

    if (matchDoc.phase < MatchPhase.BeaconsUnlocked) return false;

    if (matchDoc.outdated) return false;

    const currentCategoryFlips = matchDoc.flips?.[category] || 0;
    const totalBeaconsInCategory = matchedUser.beacon[category].length;
    if (currentCategoryFlips >= totalBeaconsInCategory) return false;

    const myTotalFlips = getTotalFlipCount(matchDoc.flips);
    const partnerTotalFlips = getTotalFlipCount(reverseMatchDoc.flips);
    const partnerTotalBeacons = getTotalBeaconCount(matchedUser.beacon);
    const myTotalBeacons = getTotalBeaconCount(myBeacons);

    if (partnerTotalFlips >= myTotalBeacons) {
      return myTotalFlips < partnerTotalBeacons;
    }

    const maxAllowedFlips = Math.min(partnerTotalFlips + 10, partnerTotalBeacons);
    return myTotalFlips < maxAllowedFlips;
  };

  const formatAnswer = (answer: YesNoValue | string[] | null, question: Question): string => {
    if (answer === null) return 'Skipped';
    if (Array.isArray(answer)) return answer.join(', ') || 'None selected';
    return answer;
  };

  const formatPreference = (answer: Answer, question: Question): string => {
    if (answer.mirrored) {
      return `Same as their answer (${formatAnswer(answer.myAnswer, question)})`;
    }
    if (answer.partnerPreference === null || answer.importance === null) return "Don't care";
    if (Array.isArray(answer.partnerPreference)) {
      return answer.partnerPreference.join(', ') || "Don't care";
    }
    return answer.partnerPreference;
  };

  const doesMatchPreference = (wanter: Answer, giver: Answer, question: Question): boolean => {
    if (wanter.importance === null) return true;

    if (wanter.mirrored) {
      if (question.type === 'yes_no') {
        return wanter.myAnswer === giver.myAnswer;
      } else if (question.type === 'multiple_choice') {
        return Array.isArray(wanter.myAnswer) && Array.isArray(giver.myAnswer) &&
          wanter.myAnswer.every(opt => giver.myAnswer?.includes(opt));
      }
    }

    if (question.type === 'yes_no') {
      return wanter.partnerPreference === giver.myAnswer;
    } else if (question.type === 'multiple_choice') {
      return Array.isArray(wanter.partnerPreference) && Array.isArray(giver.myAnswer) &&
        wanter.partnerPreference.every(opt => giver.myAnswer?.includes(opt));
    }

    return false;
  };

  const getImportanceTextColor = (importance: string | null | undefined): string => {
    if (importance === 'dealbreaker') return '#d32f2f';
    if (importance === 'important') return '#f57c00';
    if (importance === 'preference') return '#9C27B0';
    return '#666';
  };

  const capitalizeImportance = (importance: string | null | undefined): string => {
    if (importance === 'dealbreaker') return 'Dealbreaker';
    if (importance === 'important') return 'Important';
    if (importance === 'preference') return 'Preference';
    return '';
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <p>Loading match details...</p>
      </div>
    );
  }

  if (error || !matchDoc || !matchedUser) {
    return (
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <h2>Error</h2>
        <p>{error || 'Failed to load match data'}</p>
        <button
          onClick={() => navigate('/matches')}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          Back to Matches
        </button>
      </div>
    );
  }

  const hasNoBeacons = Object.values(matchedUser.beacon).every(arr => arr.length === 0);

  const getVisibleTabs = (): Array<'their_beacons' | 'my_beacons' | 'their_questions' | 'conversation'> => {
    if (!matchDoc) return ['their_questions'];

    if (matchDoc.phase < MatchPhase.BeaconsUnlocked) {
      return ['their_questions'];
    } else if (matchDoc.phase === MatchPhase.BeaconsUnlocked) {
      return ['their_beacons', 'my_beacons', 'their_questions'];
    } else if (matchDoc.phase === MatchPhase.Messaging) {
      return ['their_beacons', 'my_beacons', 'their_questions', 'conversation'];
    }

    throw new Error('Unknown phase');
  };

  const visibleTabs = getVisibleTabs();

  const partnerHasRequested = reverseMatchDoc?.beaconShareRequested || false;
  const iHaveRequested = matchDoc?.beaconShareRequested || false;

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <button
        onClick={() => navigate('/matches')}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        ← Back to Matches
      </button>

      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ marginBottom: '10px' }}>Match with: {matchDoc.matchedUserName}</h1>
        <p style={{ color: '#6c757d', marginBottom: '5px' }}>
          Matched on {matchDoc.createdAt.toDate().toLocaleDateString()}
        </p>
        {matchDoc.outdated && (
          <span
            style={{
              backgroundColor: '#fff3cd',
              color: '#856404',
              padding: '5px 10px',
              borderRadius: '4px',
              fontSize: '14px',
              display: 'inline-block'
            }}
          >
            Outdated
          </span>
        )}
      </div>

      {matchDoc && matchDoc.phase < MatchPhase.BeaconsUnlocked && partnerHasRequested && !iHaveRequested && (
        <div style={{
          backgroundColor: '#E3F2FD',
          border: '2px solid #2196F3',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <strong style={{ color: '#1976D2' }}>
            {matchDoc.matchedUserName} has requested to share beacons with you!
          </strong>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
            Click "Request to Share Beacons" below to accept and unlock beacon viewing.
          </p>
        </div>
      )}

      {matchDoc && matchDoc.phase < MatchPhase.BeaconsUnlocked && !iHaveRequested && (
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={handleBeaconShareRequest}
            disabled={requestingBeacons}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              backgroundColor: requestingBeacons ? '#ccc' : '#9C27B0',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: requestingBeacons ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            {requestingBeacons ? 'Requesting...' : 'Request to Share Beacons'}
          </button>
        </div>
      )}

      {matchDoc && matchDoc.phase === MatchPhase.WaitingForPartner && iHaveRequested && !partnerHasRequested && (
        <div style={{
          backgroundColor: '#FFF3E0',
          border: '2px solid #FF9800',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <strong style={{ color: '#F57C00' }}>
            Waiting for {matchDoc.matchedUserName} to accept beacon sharing
          </strong>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
            You've requested to share beacons. When they accept, you'll both be able to reveal each other's beacons.
          </p>
        </div>
      )}

      <div style={{ marginBottom: '30px', display: 'flex', gap: '10px' }}>
        {visibleTabs.includes('their_beacons') && (
          <button
            onClick={() => {
              setViewMode('their_beacons');
              setSelectedCategory(null);
            }}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: viewMode === 'their_beacons' ? '#2196F3' : '#e0e0e0',
              color: viewMode === 'their_beacons' ? 'white' : '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: viewMode === 'their_beacons' ? 'bold' : 'normal'
            }}
          >
            Their Beacons
          </button>
        )}
        {visibleTabs.includes('my_beacons') && (
          <button
            onClick={() => {
              setViewMode('my_beacons');
              setSelectedCategory(null);
            }}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: viewMode === 'my_beacons' ? '#2196F3' : '#e0e0e0',
              color: viewMode === 'my_beacons' ? 'white' : '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: viewMode === 'my_beacons' ? 'bold' : 'normal'
            }}
          >
            My Beacons
          </button>
        )}
        {visibleTabs.includes('their_questions') && (
          <button
            onClick={() => {
              setViewMode('their_questions');
              setSelectedCategory(null);
            }}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: viewMode === 'their_questions' ? '#2196F3' : '#e0e0e0',
              color: viewMode === 'their_questions' ? 'white' : '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: viewMode === 'their_questions' ? 'bold' : 'normal'
            }}
          >
            Their Questions
          </button>
        )}
        {visibleTabs.includes('conversation') && (
          <button
            onClick={() => {
              setViewMode('conversation');
              setSelectedCategory(null);
            }}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: viewMode === 'conversation' ? '#9C27B0' : '#e0e0e0',
              color: viewMode === 'conversation' ? 'white' : '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: viewMode === 'conversation' ? 'bold' : 'normal'
            }}
          >
            Conversation
          </button>
        )}
      </div>

      {viewMode === 'their_beacons' ? (
        hasNoBeacons ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <p>{matchDoc.matchedUserName} hasn't created any beacons yet.</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '30px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {categories.map(cat => {
                  const beaconCount = matchedUser.beacon[cat.key].length;
                  if (beaconCount === 0) return null;

                return (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(selectedCategory === cat.key ? null : cat.key)}
                    style={{
                      padding: '10px 20px',
                      fontSize: '16px',
                      backgroundColor: selectedCategory === cat.key ? '#2196F3' : '#e0e0e0',
                      color: selectedCategory === cat.key ? 'white' : '#000',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedCategory && (
            <div>
              <h2 style={{ marginBottom: '20px' }}>
                {categories.find(c => c.key === selectedCategory)?.label}
              </h2>

              {matchedUser.beacon[selectedCategory].length === 0 ? (
                <p style={{ color: '#6c757d' }}>No beacons in this category yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {matchedUser.beacon[selectedCategory].map((beacon, index) => {
                    const flipped = isFlipped(selectedCategory, index);

                    if (!flipped && index !== (matchDoc.flips?.[selectedCategory] || 0)) {
                      return null;
                    }

                    if (!flipped) {
                      const canFlip = canFlipInCategory(selectedCategory);
                      const myTotalFlips = getTotalFlipCount(matchDoc.flips);
                      const partnerTotalFlips = getTotalFlipCount(reverseMatchDoc?.flips);
                      const partnerTotalBeacons = getTotalBeaconCount(matchedUser.beacon);
                      const myTotalBeacons = myBeacons ? getTotalBeaconCount(myBeacons) : 0;

                      let flipLimitText = '';
                      if (reverseMatchDoc && partnerTotalFlips >= myTotalBeacons) {
                        flipLimitText = `Your partner revealed all ${myTotalBeacons} of your beacons! You can reveal all ${partnerTotalBeacons} of theirs.`;
                      } else if (reverseMatchDoc) {
                        const maxAllowed = Math.min(partnerTotalFlips + 10, partnerTotalBeacons);
                        flipLimitText = `You can flip ${maxAllowed - myTotalFlips} more beacons (${myTotalFlips}/${maxAllowed} used)`;
                      }

                      const isFlippingThis = flipping === selectedCategory;

                      return (
                        <div
                          key={index}
                          style={{
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #ccc',
                            borderRadius: '8px',
                            padding: '20px',
                            textAlign: 'center'
                          }}
                        >
                          <div style={{ fontSize: '32px', marginBottom: '10px' }}>🔒</div>
                          <div style={{ color: '#999', marginBottom: '10px', fontSize: '14px' }}>
                            Hidden Beacon
                          </div>
                          {flipLimitText && (
                            <div style={{ color: '#666', marginBottom: '15px', fontSize: '13px' }}>
                              {flipLimitText}
                            </div>
                          )}
                          <button
                            onClick={() => handleFlip(selectedCategory)}
                            disabled={isFlippingThis || !canFlip}
                            style={{
                              padding: '12px 24px',
                              fontSize: '16px',
                              backgroundColor: (!canFlip || isFlippingThis) ? '#ccc' : '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: (!canFlip || isFlippingThis) ? 'not-allowed' : 'pointer',
                              width: '100%',
                              maxWidth: '300px'
                            }}
                          >
                            {isFlippingThis ? 'Flipping...' : canFlip ? 'Click to Flip and Reveal' : 'Flip Limit Reached'}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={index}
                        style={{
                          backgroundColor: getTypeBackgroundColor(beacon.type),
                          border: `2px solid ${getTypeColor(beacon.type)}`,
                          borderRadius: '8px',
                          padding: '20px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '10px' }}>
                          <span
                            style={{
                              backgroundColor: 'white',
                              color: getTypeColor(beacon.type),
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              border: `2px solid ${getTypeColor(beacon.type)}`
                            }}
                          >
                            {beacon.type}
                          </span>
                        </div>
                        <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '10px 0' }} />
                        <div style={{ fontSize: '16px', lineHeight: '1.5' }}>
                          {beacon.text}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!selectedCategory && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
              <p>Select a category above to view and flip beacons</p>
            </div>
          )}
        </>
        )
      ) : viewMode === 'my_beacons' ? (
        !myBeacons || Object.values(myBeacons).every(arr => arr.length === 0) ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <p>You haven't created any beacons yet.</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '30px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {categories.map(cat => {
                  const beaconCount = myBeacons[cat.key].length;
                  if (beaconCount === 0) return null;

                  return (
                    <button
                      key={cat.key}
                      onClick={() => setSelectedCategory(selectedCategory === cat.key ? null : cat.key)}
                      style={{
                        padding: '10px 20px',
                        fontSize: '16px',
                        backgroundColor: selectedCategory === cat.key ? '#2196F3' : '#e0e0e0',
                        color: selectedCategory === cat.key ? 'white' : '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                    >
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedCategory && (
              <div>
                <h2 style={{ marginBottom: '20px' }}>
                  {categories.find(c => c.key === selectedCategory)?.label}
                </h2>

                {myBeacons[selectedCategory].length === 0 ? (
                  <p style={{ color: '#6c757d' }}>No beacons in this category yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {myBeacons[selectedCategory].map((beacon, index) => {
                      const flipped = isMyBeaconFlipped(selectedCategory, index);

                      return (
                        <div
                          key={index}
                          style={{
                            backgroundColor: flipped ? getTypeBackgroundColor(beacon.type) : '#f5f5f5',
                            border: flipped ? `2px solid ${getTypeColor(beacon.type)}` : '2px solid #ccc',
                            borderRadius: '8px',
                            padding: '20px',
                            opacity: flipped ? 1 : 0.5
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '10px' }}>
                            <span
                              style={{
                                backgroundColor: 'white',
                                color: flipped ? getTypeColor(beacon.type) : '#999',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                border: flipped ? `2px solid ${getTypeColor(beacon.type)}` : '2px solid #ccc'
                              }}
                            >
                              {beacon.type}
                            </span>
                          </div>
                          <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '10px 0' }} />
                          <div style={{ fontSize: '16px', lineHeight: '1.5', color: flipped ? '#000' : '#999' }}>
                            {beacon.text}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {!selectedCategory && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                <p>Select a category above to see which beacons {matchDoc.matchedUserName} has flipped</p>
              </div>
            )}
          </>
        )
      ) : viewMode === 'conversation' ? (
        <div>
          {!canSendMessage() && (
            <div style={{
              backgroundColor: '#FFF3E0',
              border: '2px solid #FF9800',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <strong style={{ color: '#F57C00' }}>Rate Limit Active</strong>
              <p style={{ margin: '5px 0 0 0' }}>
                Next message in {getRateLimitTimeRemaining()}
              </p>
            </div>
          )}

          <div style={{
            backgroundColor: '#f5f5f5',
            border: '2px solid #ccc',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3>Send a Message</h3>
            <textarea
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              placeholder="Type your message here (400-4000 characters)..."
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '10px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                resize: 'vertical'
              }}
              disabled={!canSendMessage()}
            />
            <div style={{
              marginTop: '10px',
              fontSize: '14px',
              color: getCharacterCountColor(draftMessage.length)
            }}>
              {draftMessage.length} / {MAX_CHARS} characters
              {draftMessage.length < MIN_CHARS && ` (minimum ${MIN_CHARS})`}
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!isValidMessageLength(draftMessage) || !canSendMessage() || sendingMessage}
              style={{
                marginTop: '15px',
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: (!isValidMessageLength(draftMessage) || !canSendMessage()) ? '#ccc' : '#9C27B0',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (!isValidMessageLength(draftMessage) || !canSendMessage()) ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {sendingMessage ? 'Sending...' : 'Send Message (5 min grace period)'}
            </button>
          </div>

          <div>
            <h3>Conversation History</h3>
            {messages.filter(m => m.status !== 'draft' || m.senderId === user?.uid).length === 0 ? (
              <p style={{ color: '#6c757d' }}>No messages yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {messages
                  .filter(m => m.status !== 'draft' || m.senderId === user?.uid)
                  .map(message => {
                    const isOwnMessage = message.senderId === user?.uid;
                    const isInGracePeriod = message.status === 'draft' && gracePeriodRemaining[message.messageId] !== undefined;

                    return (
                      <div
                        key={message.messageId}
                        style={{
                          backgroundColor: isOwnMessage ? '#E3F2FD' : '#F3E5F5',
                          border: `2px solid ${isOwnMessage ? '#2196F3' : '#9C27B0'}`,
                          padding: '15px',
                          borderRadius: '8px',
                          alignSelf: isOwnMessage ? 'flex-end' : 'flex-start',
                          maxWidth: '70%'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <strong>{message.senderName}</strong>
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            {message.sentAt ? message.sentAt.toDate().toLocaleString() : 'Sending...'}
                          </span>
                        </div>

                        {isInGracePeriod && isOwnMessage && (
                          <div style={{
                            backgroundColor: '#FFF3E0',
                            padding: '8px',
                            borderRadius: '4px',
                            marginBottom: '10px',
                            fontSize: '13px',
                            color: '#F57C00'
                          }}>
                            ⏱️ Grace period: {Math.floor(gracePeriodRemaining[message.messageId]! / 60)}m {gracePeriodRemaining[message.messageId]! % 60}s
                          </div>
                        )}

                        <div style={{ lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                          {message.content}
                        </div>

                        {message.status === 'edited' && (
                          <div style={{ marginTop: '10px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                            (Edited)
                          </div>
                        )}

                        {message.editHistory.length > 0 && (
                          <button
                            onClick={() => setShowEditHistory(message.messageId)}
                            style={{
                              marginTop: '10px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              backgroundColor: 'transparent',
                              color: '#2196F3',
                              border: '1px solid #2196F3',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            View Edit History ({message.editHistory.length})
                          </button>
                        )}

                        {isOwnMessage && (
                          <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                            {isInGracePeriod && (
                              <>
                                <button onClick={() => handleCancelMessage(message.messageId)} style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#d32f2f',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}>
                                  Cancel
                                </button>
                                <button onClick={() => {
                                  setEditingMessageId(message.messageId);
                                  setEditContent(message.content);
                                }} style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#f57c00',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}>
                                  Edit
                                </button>
                              </>
                            )}

                            {message.status !== 'draft' && (
                              <button onClick={() => {
                                setEditingMessageId(message.messageId);
                                setEditContent(message.content);
                              }} style={{
                                padding: '6px 12px',
                                backgroundColor: '#f57c00',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}>
                                Edit
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {editingMessageId && (
            <div style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '90%'
              }}>
                <h3>Edit Message</h3>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '150px',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '4px',
                    border: '1px solid #ccc'
                  }}
                />
                <div style={{ marginTop: '10px', fontSize: '14px', color: getCharacterCountColor(editContent.length) }}>
                  {editContent.length} / {MAX_CHARS} characters
                </div>
                <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => {
                      const msg = messages.find(m => m.messageId === editingMessageId);
                      if (msg?.status === 'draft') {
                        handleSaveDraftEdit();
                      } else {
                        handleSaveSentEdit();
                      }
                    }}
                    disabled={!isValidMessageLength(editContent)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: isValidMessageLength(editContent) ? '#9C27B0' : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isValidMessageLength(editContent) ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingMessageId(null);
                      setEditContent('');
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {showEditHistory && (() => {
            const message = messages.find(m => m.messageId === showEditHistory);
            if (!message) return null;

            return (
              <div style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  maxWidth: '600px',
                  maxHeight: '80vh',
                  overflow: 'auto'
                }}>
                  <h3>Edit History</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div style={{
                      backgroundColor: '#E3F2FD',
                      border: '2px solid #2196F3',
                      padding: '15px',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>Current Version</div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
                    </div>
                    {message.editHistory.map((entry, index) => (
                      <div key={index} style={{
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ccc',
                        padding: '15px',
                        borderRadius: '8px'
                      }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                          {entry.editedAt.toDate().toLocaleString()}
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{entry.content}</div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowEditHistory(null)}
                    style={{
                      marginTop: '20px',
                      padding: '10px 20px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        questionsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading questions...</p>
          </div>
        ) : (() => {
          const answeredQuestions = questions.filter(q => {
            const theirAnswer = matchedUserAnswers[q.id];
            if (!theirAnswer) return false;

            if (q.type === 'yes_no') {
              return theirAnswer.myAnswer !== null;
            } else if (q.type === 'multiple_choice') {
              return Array.isArray(theirAnswer.myAnswer) && theirAnswer.myAnswer.length > 0;
            }
            return false;
          });

          const categorizedQuestions = answeredQuestions.filter(q => {
            const categories = q.category.length > 0 ? q.category : ['Uncategorized'];
            return categories.some(cat => selectedQuestionCategories[cat] !== false);
          });

          const sortedQuestions = [...categorizedQuestions].sort((a, b) => {
            const importanceValue = (imp: string | null | undefined) => {
              if (imp === 'dealbreaker') return 3;
              if (imp === 'important') return 2;
              if (imp === 'preference') return 1;
              return 0;
            };

            const aImportance = importanceValue(matchedUserAnswers[a.id]?.importance);
            const bImportance = importanceValue(matchedUserAnswers[b.id]?.importance);

            if (aImportance !== bImportance) return bImportance - aImportance;
            return a.class - b.class;
          });

          const availableCategories = Array.from(
            new Set(
              answeredQuestions.flatMap(q =>
                q.category.length > 0 ? q.category : ['Uncategorized']
              )
            )
          ).sort();

          return (
            <>
              {availableCategories.length > 0 && (
                <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
                    Filter by category:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {availableCategories.map(category => (
                      <label key={category} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedQuestionCategories[category] !== false}
                          onChange={(e) => {
                            setSelectedQuestionCategories(prev => ({
                              ...prev,
                              [category]: e.target.checked
                            }));
                          }}
                          style={{ marginRight: '6px' }}
                        />
                        <span>{category || 'Uncategorized'}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {sortedQuestions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  <p>{matchDoc.matchedUserName} hasn't answered any questions yet{availableCategories.length > 0 ? ' (or all their answers are filtered out)' : ''}.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {sortedQuestions.map((question) => {
                    const theirAnswer = matchedUserAnswers[question.id];
                    const myAnswer = myAnswers[question.id];

                    const iAnswered = myAnswer && (
                      question.type === 'yes_no'
                        ? myAnswer.myAnswer !== null
                        : Array.isArray(myAnswer.myAnswer) && myAnswer.myAnswer.length > 0
                    );

                    if (!iAnswered) {
                      return (
                        <div
                          key={question.id}
                          style={{
                            backgroundColor: '#f5f5f5',
                            border: '2px solid #ccc',
                            borderRadius: '8px',
                            padding: '20px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                            <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                              Class {question.class}
                            </div>
                            {theirAnswer.importance && (
                              <div
                                style={{
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  color: getImportanceTextColor(theirAnswer.importance)
                                }}
                              >
                                {capitalizeImportance(theirAnswer.importance)}
                              </div>
                            )}
                          </div>

                          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '20px' }}>
                            {question.text}
                          </div>

                          <div style={{ marginBottom: '15px' }}>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#555' }}>
                              Their Answer:
                            </div>
                            <div
                              style={{
                                backgroundColor: '#E3F2FD',
                                border: '1px solid #90CAF9',
                                borderRadius: '4px',
                                padding: '12px',
                                fontSize: '15px'
                              }}
                            >
                              {formatAnswer(theirAnswer.myAnswer, question)}
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#555' }}>
                              What They Want From Partner:
                            </div>
                            <div
                              style={{
                                backgroundColor: '#E3F2FD',
                                border: '1px solid #90CAF9',
                                borderRadius: '4px',
                                padding: '12px',
                                fontSize: '15px'
                              }}
                            >
                              {formatPreference(theirAnswer, question)}
                              {theirAnswer.importance && theirAnswer.importance !== null && (
                                <span style={{ marginLeft: '8px', color: getImportanceTextColor(theirAnswer.importance), fontWeight: 'bold' }}>
                                  ({capitalizeImportance(theirAnswer.importance)})
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Their note */}
                          {theirAnswer.note && (
                            <div style={{ marginTop: '15px' }}>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#555' }}>
                                Their Note:
                              </div>
                              <div
                                style={{
                                  backgroundColor: '#FFF9E6',
                                  border: '1px solid #FFE082',
                                  borderRadius: '4px',
                                  padding: '12px',
                                  fontSize: '14px',
                                  whiteSpace: 'pre-wrap',
                                  fontStyle: 'italic',
                                }}
                              >
                                {theirAnswer.note}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }

                    const iMatchTheirPreference = doesMatchPreference(theirAnswer, myAnswer, question);
                    const theyMatchMyPreference = doesMatchPreference(myAnswer, theirAnswer, question);

                    return (
                      <div
                        key={question.id}
                        style={{
                          backgroundColor: '#f5f5f5',
                          border: '2px solid #ccc',
                          borderRadius: '8px',
                          padding: '20px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                          <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                            Class {question.class}
                          </div>
                          {(theirAnswer.importance === 'dealbreaker' || myAnswer.importance === 'dealbreaker') && (
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#d32f2f' }}>
                              Dealbreaker 🔥
                            </div>
                          )}
                        </div>

                        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '20px' }}>
                          {question.text}
                        </div>

                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr',
                            gap: '20px'
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: '#E3F2FD',
                              border: '1px solid #90CAF9',
                              borderRadius: '8px',
                              padding: '15px'
                            }}
                          >
                            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#555' }}>
                              What I Want
                            </div>

                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '6px', color: '#666' }}>
                                My Preference:
                              </div>
                              <div style={{ fontSize: '14px' }}>
                                {formatPreference(myAnswer, question)}
                              </div>
                              {myAnswer.importance && (
                                <div style={{ fontSize: '12px', color: getImportanceTextColor(myAnswer.importance), fontWeight: 'bold', marginTop: '4px' }}>
                                  ({capitalizeImportance(myAnswer.importance)})
                                </div>
                              )}
                            </div>

                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '6px', color: '#666' }}>
                                They Gave Me:
                              </div>
                              <div style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>→ {formatAnswer(theirAnswer.myAnswer, question)}</span>
                                {theyMatchMyPreference && <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✓</span>}
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              backgroundColor: '#E3F2FD',
                              border: '1px solid #90CAF9',
                              borderRadius: '8px',
                              padding: '15px'
                            }}
                          >
                            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#555' }}>
                              What They Want
                            </div>

                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '6px', color: '#666' }}>
                                Their Preference:
                              </div>
                              <div style={{ fontSize: '14px' }}>
                                {formatPreference(theirAnswer, question)}
                              </div>
                              {theirAnswer.importance && (
                                <div style={{ fontSize: '12px', color: getImportanceTextColor(theirAnswer.importance), fontWeight: 'bold', marginTop: '4px' }}>
                                  ({capitalizeImportance(theirAnswer.importance)})
                                </div>
                              )}
                            </div>

                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '6px', color: '#666' }}>
                                I Gave Them:
                              </div>
                              <div style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>→ {formatAnswer(myAnswer.myAnswer, question)}</span>
                                {iMatchTheirPreference && <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✓</span>}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Notes section */}
                        {(theirAnswer.note || myAnswer.note) && (
                          <div style={{
                            marginTop: '20px',
                            paddingTop: '20px',
                            borderTop: '1px solid #ccc',
                          }}>
                            {theirAnswer.note && (
                              <div style={{ marginBottom: myAnswer.note ? '15px' : '0' }}>
                                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                                  Their Note:
                                </div>
                                <div
                                  style={{
                                    backgroundColor: '#FFF9E6',
                                    border: '1px solid #FFE082',
                                    borderRadius: '4px',
                                    padding: '12px',
                                    fontSize: '14px',
                                    whiteSpace: 'pre-wrap',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  {theirAnswer.note}
                                </div>
                              </div>
                            )}

                            {myAnswer.note && (
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                                  My Note:
                                </div>
                                <div
                                  style={{
                                    backgroundColor: '#E8F5E9',
                                    border: '1px solid #A5D6A7',
                                    borderRadius: '4px',
                                    padding: '12px',
                                    fontSize: '14px',
                                    whiteSpace: 'pre-wrap',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  {myAnswer.note}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()
      )}
    </div>
  );
}

export default MatchDetail;
